import { Injectable } from '@angular/core';
import { AppHttpService } from './app-http.service';
import { Organization, Project, Report } from '../models';
import { HttpResponse } from '@angular/common/http';
import { saveAs } from 'file-saver';

@Injectable({ providedIn: 'root' })
export class ReportService {

  constructor(
    private appHttpService: AppHttpService,
  ) { }

  /**
   * Returns Templates
   * @param qp query params
   * @returns {Promise<Report[]>}
   */
  getReports(project: Project = null, org: Organization = null, qp: any = {}) {
    const path = project?.id
      ? `project/${project?.id}`
      : `organization/${org?.id}`;
    return this.appHttpService
      .getService(`/v2/${path}/reports`, qp)
      .toPromise()
      .then(resp => {
        if (resp.data && Array.isArray(resp.data)) {
          return resp.data.map((r) => new Report(r));
        }
        throw new Error('Error parsing response');
      });
  }

  /**
   * Creates a new Report
   * @param payload json payload
   * @param qp query params
   * @returns {Promise<Report>}
   */
  create(project: Project = null, org: Organization = null, payload: any = null, qp: any = {}): Promise<Report> {
    const path = project?.id
      ? `project/${project?.id}`
      : `organization/${org?.id}`;
    return this.appHttpService
      .postService(`/v2/${path}/report`, payload, qp)
      .toPromise()
      .then((resp: any) => {
        if (resp.data) {
          return new Report(resp.data);
        }
        throw new Error('Error parsing response');
      });
  }

  /**
   * Updates Report
   * @param payload json payload
   * @param qp query params
   * @returns {Promise<Report>}
   */
  update(payload: any = null, qp: any = {}): Promise<Report> {
    return this.appHttpService
      .putService(`/v2/report/${payload?.id}`, payload, qp)
      .toPromise()
      .then((resp: any) => {
        if (resp.data) {
          return new Report(resp.data);
        }
        throw new Error('Error parsing response');
      });
  }

  /**
   * Deletes Report
   * @param model Report
   * @returns {Promise<any>} {status, message}
   */
  delete(model: Report): Promise<HttpResponse<any>> {
    return this.appHttpService
      .deleteService(`/v2/report/${model?.id}`)
      .toPromise();
  }

  /**
   * Renders report as html
   * @param payload {id, siteId, projectId, date, from, to, attachments, original_size_images}
   * @returns {Promise<{date, title, html}>}
   */
  renderReport(payload) {
    // Extract reportId separately from other parameters
    const { reportId, ...otherParams } = payload;
    
    const qp = {
      // Org-scoped reports have no site/project — only send these when truthy
      // so existing project reports remain byte-identical.
      ...(payload.siteId ? { siteId: payload.siteId } : {}),
      ...(payload.projectId ? { projectId: payload.projectId } : {}),
      ...(payload.date ? { date: payload.date } : {}),
      ...(payload.from && payload.to ? { from: payload.from, to: payload.to } : {}),
      ...(payload.status ? { status: payload.status } : {}),
      ...(payload.accounts ? { accounts: payload.accounts } : {}),
      ...(payload.attachments ? { attachments: payload.attachments } : {}),
      ...(payload.original_size_images ? { original_size_images: payload.original_size_images } : {}),
      ...(payload.download ? { download: true } : {}),
    };
    
    // Add any additional dynamic parameters that aren't in the standard set
    const standardParams = ['reportId', 'siteId', 'projectId', 'date', 'from', 'to', 'status', 'accounts', 'attachments', 'original_size_images', 'download'];
    Object.keys(otherParams).forEach(key => {
      if (!standardParams.includes(key) && otherParams[key] !== null && otherParams[key] !== undefined) {
        qp[key] = otherParams[key];
      }
    });

    if (payload.download) {
      // Request as 'blob' but with observe: 'response' to check status
      return this.appHttpService
        .getService(`/v2/report/${payload.reportId}/render`, qp, {
          responseType: 'blob' as 'json',
          observe: 'response'
        })
        .toPromise()
        .then(async (resp: any) => {
          // Check if this is a 202 response (deferred)
          if (resp.status === 202 && resp.body) {
            // Parse the blob as JSON for deferred response
            const text = await resp.body.text();
            try {
              const jsonResponse = JSON.parse(text);
              if (jsonResponse.jobId && jsonResponse.message) {
                return {
                  jobId: jsonResponse.jobId,
                  message: jsonResponse.message,
                  details: jsonResponse.details || jsonResponse.message
                };
              }
            } catch (e) {
              // If parsing fails, treat as regular blob
            }
          }
          
          // For successful file download (200 status)
          if (resp.status === 200 && resp.body instanceof Blob) {
            const ext = payload.format === 'excel' ? 'xlsx' : 'pdf';
            return saveAs(resp.body, `Download Report.${ext}`);
          }
          
          // Default case
          return resp.body;
        });
    } else {
      return this.appHttpService
        .getService(`/v2/report/${payload.reportId}/render`, qp)
        .toPromise()
        .then((resp: any) => {
          // Check if this is a deferred response
          if (resp?.jobId && resp?.message) {
            return {
              jobId: resp.jobId,
              message: resp.message,
              details: resp.details || resp.message
            };
          }
          return {
            html: resp?.html,
            data: resp?.data || [],
            // Per-card render metadata (groupKey) — needed so a per-card Excel
            // download can slice to the right content group.
            metadata: resp?.metadata,
          };
        });
    }
  }

  /**
   * Resolve an entity report template for a project by `report_type`.
   * Scans project + org reports and returns a project-scoped match first,
   * falling back to an org-scoped one. Returns null when none exist — callers
   * should surface a "contact your administrator" message in that case.
   */
  async resolveEntityReport(projectId: string, reportType: string): Promise<Report | null> {
    if (!projectId || !reportType) {
      return null;
    }
    try {
      const reports = await this.getReports(
        { id: projectId } as any,
        null,
        {},
      );
      const matches = reports.filter((r: any) => r.report_type === reportType);
      if (!matches.length) {
        return null;
      }
      // Prefer project-scoped match, then org-scoped, then anything remaining.
      const projectScoped = matches.find((r: any) => r.project_id === projectId || r.project?.id === projectId);
      if (projectScoped) return projectScoped;
      return matches[0];
    } catch {
      return null;
    }
  }

  /**
   * Download report as pdf
   * @param report Report object
   * @param siteOrSites Site object or array of site IDs
   * @param project Project object
   * @param dateParams Date parameters
   * @param attachments Include attachments
   * @param originalSizeImages Use original size images
   */
  downloadReport(report, siteOrSites, project, dateParams, attachments = false, originalSizeImages = false, format = 'pdf') {
    // Handle both single site object and array of site IDs
    const isSingleSite = siteOrSites && !Array.isArray(siteOrSites);
    const isMultipleSites = Array.isArray(siteOrSites) && siteOrSites.length > 0;

    const qp = {
      ...(isSingleSite ? { siteId: siteOrSites?.id } : {}),
      ...(isMultipleSites ? { siteIds: siteOrSites } : {}),
      projectId: project.id,
      ...dateParams, // Include all dateParams properties
      ...(attachments ? { attachments: attachments } : {}),
      ...(originalSizeImages ? { original_size_images: originalSizeImages } : {}),
      download: true,
      ...(format && format !== 'pdf' ? { format } : {}),
    };

    const extension = format === 'excel' ? 'xlsx' : 'pdf';
    const filename = `${report?.name || 'report'}.${extension}`;

    // Request as 'blob' but with observe: 'response' to check status
    return this.appHttpService
      .getService(`/v2/report/${report?.id}/render`, qp, {
        responseType: 'blob' as 'json',
        observe: 'response'
      })
      .toPromise()
      .then(async (resp: any) => {
        // Check if this is a 202 response (deferred)
        if (resp.status === 202 && resp.body) {
          // Parse the blob as JSON for deferred response
          const text = await resp.body.text();
          try {
            const jsonResponse = JSON.parse(text);
            if (jsonResponse.jobId && jsonResponse.message) {
              return {
                jobId: jsonResponse.jobId,
                message: jsonResponse.message,
                details: jsonResponse.details || jsonResponse.message
              };
            }
          } catch (e) {
            // If parsing fails, treat as regular blob
          }
        }

        // For successful file download (200 status)
        if (resp.status === 200 && resp.body instanceof Blob) {
          // Use server-provided filename from Content-Disposition if available
          let downloadFilename = filename;
          const contentDisposition = resp.headers?.get('Content-Disposition');
          if (contentDisposition) {
            const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (match && match[1]) {
              downloadFilename = match[1].replace(/['"]/g, '');
            }
          } else {
            // Fall back to Content-Type to determine extension (e.g., zip downloads)
            const contentType = resp.headers?.get('Content-Type');
            if (contentType === 'application/zip') {
              downloadFilename = `${report?.name || 'report'}.zip`;
            }
          }
          return saveAs(resp.body, downloadFilename);
        }

        // Default case
        return resp.body;
      });
  }
}
